import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Calendar, Package, Settings, BarChart3, Users, Warehouse, ClipboardCheck, Truck, Bell, Settings2, Menu, X, LogOut } from 'lucide-react';
import { BilingualText } from './BilingualText';
import { useAuth } from '../context/AuthContext';
interface LayoutProps {
  children: React.ReactNode;
}
export function Layout({
  children
}: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    logout
  } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Completely hide layout elements on the login page
  if (location.pathname === '/login') {
    return <main className="min-h-screen" data-id="element-69">{children}</main>;
  }
  if (!user) {
    return <>{children}</>;
  }
  const allMenuItems = [{
    icon: LayoutDashboard,
    en: 'Dashboard',
    ar: 'لوحة التحكم',
    path: '/',
    roles: ['ADMIN', 'STAFF', 'SUPPLIER']
  }, {
    icon: Calendar,
    en: 'Appointments',
    ar: 'المواعيد',
    path: '/appointments',
    roles: ['ADMIN', 'STAFF', 'SUPPLIER']
  }, {
    icon: Package,
    en: 'Shipments',
    ar: 'الشحنات',
    path: '/kanban',
    roles: ['ADMIN', 'STAFF', 'SUPPLIER']
  }, {
    icon: Settings,
    en: 'Capacity',
    ar: 'السعة',
    path: '/capacity',
    roles: ['ADMIN', 'STAFF']
  }, {
    icon: BarChart3,
    en: 'Reports',
    ar: 'التقارير',
    path: '/reports',
    roles: ['ADMIN', 'STAFF']
  }, {
    icon: Users,
    en: 'Users',
    ar: 'المستخدمين',
    path: '/users',
    roles: ['ADMIN']
  }, {
    icon: Warehouse,
    en: 'Warehouses',
    ar: 'المستودعات',
    path: '/warehouses',
    roles: ['ADMIN']
  }, {
    icon: ClipboardCheck,
    en: 'Approvals',
    ar: 'الموافقات',
    path: '/approvals',
    roles: ['ADMIN', 'STAFF']
  }, {
    icon: Truck,
    en: 'Receiving',
    ar: 'الاستلام',
    path: '/receiving',
    roles: ['ADMIN', 'STAFF']
  }, {
    icon: Bell,
    en: 'Notifications',
    ar: 'التنبيهات',
    path: '/notifications',
    roles: ['ADMIN', 'STAFF', 'SUPPLIER']
  }, {
    icon: Settings2,
    en: 'System Settings',
    ar: 'إعدادات النظام',
    path: '/system-settings',
    roles: ['ADMIN']
  }];
  const menuItems = allMenuItems.filter(item => user && item.roles.includes(user.role));
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  return <div className="min-h-screen bg-slate-50" data-id="element-70">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-200 fixed h-full left-0 top-0 z-30" data-id="element-71">
        <div className="p-6 border-b border-slate-100 flex items-center gap-3" data-id="element-72">
          <img src="https://cdn.magicpatterns.com/uploads/8gwbCWtCALwvXSSg9wjHzH/ChatGPT_Image_Jul_11,_2026,_08_45_11_PM.png" alt="DAS Logo" className="h-12 w-auto" data-id="element-73" />
          <div className="flex flex-col" data-id="element-74">
            <span className="text-2xl font-black text-[#2E3192] leading-none" data-id="element-75">
              DAS
            </span>
            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-tighter leading-tight mt-0.5" data-id="element-76">
              Dock Appointment Scheduling
            </span>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-id="element-77">
          {menuItems.map(item => <Link key={item.path} to={item.path} data-id="element-78">
              <SidebarItem icon={item.icon} en={item.en} ar={item.ar} active={location.pathname === item.path} data-id="element-79" />
            </Link>)}
        </nav>
        <div className="p-4 border-t border-slate-100 space-y-2" data-id="element-80">
          <div className="flex items-center gap-3 p-2" data-id="element-81">
            <div className="w-10 h-10 rounded-full bg-[#2E3192] text-white flex items-center justify-center font-bold" data-id="element-82">
              {user?.name.split(' ').map(n => n[0]).join('')}
            </div>
            <div className="flex flex-col" data-id="element-83">
              <span className="font-bold text-sm" data-id="element-84">{user?.name}</span>
              <span className="text-[10px] text-slate-400 uppercase font-bold" data-id="element-85">
                {user?.role}
              </span>
            </div>
          </div>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors text-sm font-bold" data-id="element-86">
            <LogOut size={18} data-id="element-87" />
            <span data-id="element-88">Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 bg-white border-b border-slate-200 z-20 px-4 py-3" data-id="element-89">
        <div className="flex items-center justify-between" data-id="element-90">
          <div className="flex items-center gap-3" data-id="element-91">
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 text-slate-600 hover:bg-slate-50 rounded-lg" data-id="element-92">
              {isMobileMenuOpen ? <X size={24} data-id="element-93" /> : <Menu size={24} data-id="element-94" />}
            </button>
            <img src="https://cdn.magicpatterns.com/uploads/8gwbCWtCALwvXSSg9wjHzH/ChatGPT_Image_Jul_11,_2026,_08_45_11_PM.png" alt="DAS Logo" className="h-8 w-auto" data-id="element-95" />
            <div className="flex flex-col" data-id="element-96">
              <span className="text-lg font-black text-[#2E3192] leading-none" data-id="element-97">
                DAS
              </span>
              <span className="text-[6px] font-bold text-slate-400 uppercase tracking-tighter leading-tight" data-id="element-98">
                Dock Appointment Scheduling
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-99">
            <button className="p-2 text-slate-500 hover:bg-slate-100 rounded-full relative" data-id="element-100">
              <Bell size={20} data-id="element-101" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white" data-id="element-102"></span>
            </button>
            <div className="w-8 h-8 rounded-full bg-[#2E3192] text-white flex items-center justify-center font-bold text-xs" data-id="element-103">
              {user?.name.split(' ').map(n => n[0]).join('')}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && <div className="lg:hidden fixed inset-0 bg-slate-900/50 z-40" onClick={() => setIsMobileMenuOpen(false)} data-id="element-104">
          <aside className="w-64 bg-white h-full flex flex-col animate-in slide-in-from-left duration-300" onClick={e => e.stopPropagation()} data-id="element-105">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center" data-id="element-106">
              <div className="flex items-center gap-3" data-id="element-107">
                <img src="https://cdn.magicpatterns.com/uploads/8gwbCWtCALwvXSSg9wjHzH/ChatGPT_Image_Jul_11,_2026,_08_45_11_PM.png" alt="DAS Logo" className="h-10 w-auto" data-id="element-108" />
                <div className="flex flex-col" data-id="element-109">
                  <span className="text-xl font-black text-[#2E3192] leading-none" data-id="element-110">
                    DAS
                  </span>
                  <span className="text-[7px] font-bold text-slate-400 uppercase tracking-tighter leading-tight" data-id="element-111">
                    Dock Appointment Scheduling
                  </span>
                </div>
              </div>
              <button onClick={() => setIsMobileMenuOpen(false)} className="text-slate-400" data-id="element-112">
                <X size={20} data-id="element-113" />
              </button>
            </div>
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-id="element-114">
              {menuItems.map(item => <Link key={item.path} to={item.path} onClick={() => setIsMobileMenuOpen(false)} data-id="element-115">
                  <SidebarItem icon={item.icon} en={item.en} ar={item.ar} active={location.pathname === item.path} data-id="element-116" />
                </Link>)}
            </nav>
          </aside>
        </div>}

      {/* Main Content Area */}
      <div className="lg:pl-64 min-h-screen flex flex-col" data-id="element-117">
        {/* Desktop Header */}
        <header className="hidden lg:block sticky top-0 bg-white border-b border-slate-200 z-10 px-8 py-4" data-id="element-118">
          <div className="flex items-center justify-between max-w-7xl mx-auto" data-id="element-119">
            <div className="flex items-center gap-6" data-id="element-120">
              <div className="flex flex-col" data-id="element-121">
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider" data-id="element-122">
                  Current Warehouse
                </span>
                <span className="font-bold text-slate-700" data-id="element-123">
                  Riyadh Main (WH-01)
                </span>
              </div>
              <div className="h-8 w-px bg-slate-100" data-id="element-124" />
              <div className="flex flex-col" data-id="element-125">
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider" data-id="element-126">
                  Date
                </span>
                <span className="font-bold text-slate-700" data-id="element-127">July 11, 2026</span>
              </div>
            </div>
            <div className="flex items-center gap-4" data-id="element-128">
              <button className="p-2 text-slate-500 hover:bg-slate-100 rounded-full relative" data-id="element-129">
                <Bell size={20} data-id="element-130" />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white" data-id="element-131"></span>
              </button>
              <div className="h-8 w-px bg-slate-100 mx-1" data-id="element-132" />
              <button className="text-xs font-bold text-[#2E3192] hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors" data-id="element-133">
                EN / AR
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1" data-id="element-134">{children}</main>
      </div>
    </div>;
}
function SidebarItem({
  icon: Icon,
  en,
  ar,
  active = false
}: {
  icon: any;
  en: string;
  ar: string;
  active?: boolean;
}) {
  return <div className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${active ? 'bg-[#2E3192] text-white shadow-lg shadow-blue-900/20' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-135">
      <Icon size={20} className={active ? 'text-white' : 'text-slate-400'} data-id="element-136" />
      <BilingualText en={en} ar={ar} className={`font-medium ${active ? 'text-white' : ''}`} data-id="element-137" />
    </div>;
}